import { api } from '../axios';

import {
  Messages,
  ObjectiveHistory,
  FetchObjectivesParams,
  ObjectiveHistoryWhere,
  SaveHistoryParams,
  FetchHistoryParams,
  Objective,
  headers,
  EveResponse,
  NoAuthError,
  PostMultipleHistories,
} from '../../types';

import Authentication from './Authentication';

const QUESTIONS_ENDPOINT = '/questions';
const QUESTIONS_ENDPOINT_RANDOM = '/questions/list';
const QUESTIONS_HISTORY_ENDPOINT = '/history/question';
const QUESTIONS_YEAR_ENDPOINT = '/service/distinct/questions/info.year';
const QUESTIONS_JURY_ENDPOINT = '/service/distinct/questions/info.jury';
export const QUESTION_REPORT_ENDPOINT = '/email/error';

import { cloneDeep } from 'lodash';
import Mongo from './Mongo';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import NotifyHelper from '../NotifyHelper';
import Area from './Area';
import Journey from './Journey';

export type UpdatedQuestionHistory = {
  data: {
    _etag: string;
    _id: string;
    _updated: string;
    _created: string;
    _status: string;
  };
};

class Objectives extends Mongo {
  public cancelToken: CancelTokenSource | undefined = undefined;

  public async fetch(
    { where = {}, page = 1, max = 150, history = 0 }: FetchObjectivesParams,
    showError = false
  ) {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      if (!loginData.contest?._id) return;
      const flattenQuery = this.flattenMongoQuery({ query: where }) as unknown;

      const axiosConfig: AxiosRequestConfig = {
        params: {
          page,
          max_results: max,
          where: flattenQuery,
        },
        headers: {
          ContestId: loginData.contest._id,
          Useremail: loginData.user.email,
          Userstatus: history,
        },
      };

      const response: AxiosResponse<EveResponse<Objective[]>> = await api.get(
        QUESTIONS_ENDPOINT_RANDOM,
        axiosConfig
      );
      if (!response.data._items.length) return;
      return response;
    } catch (error) {
      if (showError) {
        NotifyHelper.notifyError(Messages.FETCH_QUESTIONS_ERROR);
      }
    }
  }

  public async sendReport(msg: string, question: Objective) {
    try {
      const loginData = Authentication.loggedUser();
      const loggedArea = Area.loggedArea();
      if (!loginData || !loggedArea) return;
      await api.post(
        QUESTION_REPORT_ENDPOINT,
        {
          area: loginData.area.name,
          user: loginData.user,
          question: question,
          report: msg,
        },
        {
          headers: {
            'x-org-id': loggedArea.group,
          },
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchYears(
    showError = false
  ): Promise<AxiosResponse<EveResponse<number[]>> | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      if (!loginData.contest?._id) return;
      const response: AxiosResponse<EveResponse<number[]>> = await api.get(
        QUESTIONS_YEAR_ENDPOINT,
        {
          headers: {
            ContestId: loginData.contest._id,
            userEmail: loginData.user.email,
          },
        }
      );
      if (!response.data._items.length) return;
      return response;
    } catch (error) {
      if (showError) {
        NotifyHelper.notifyError(Messages.FETCH_INFO_YEAR_ERROR);
      }
    }
  }

  public async fetchJury(
    showError = false
  ): Promise<EveResponse<string[]> | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      if (!loginData.contest?._id) return;
      const response = await api.get<EveResponse<string[]>>(
        QUESTIONS_JURY_ENDPOINT,
        {
          params: {
            max_results: 1000,
          },
          headers: {
            ContestId: loginData.contest._id,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (showError) {
        NotifyHelper.notifyError(Messages.FETCH_INFO_YEAR_ERROR);
      }
    }
  }

  public async updateJourneyObjectives() {
    const sessionData = Authentication.loggedUser();
    if (!sessionData) return;
    const id = sessionData.journey._id;
    if (id && !sessionData.journey.objectives) {
      await Journey.update(id, {
        objectives: true,
      });
      sessionData.journey.objectives = true;
      Authentication.storeUser(sessionData);
    }
  }

  public async saveQuestionHistory(
    question: SaveHistoryParams,
    showError = false
  ): Promise<UpdatedQuestionHistory | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;

      if (question.history?._id) {
        const payload: ObjectiveHistoryWhere = cloneDeep<ObjectiveHistoryWhere>(
          question.history
        );
        const id = question.history._id;
        const response = await api.patch(
          `${QUESTIONS_HISTORY_ENDPOINT}/${id}`,
          {
            ...this.removeMongoAttrs(payload),
            user_email: loginData.user.email,
            scored: question.correct,
            origin: question.origin,
            origin_id: question.origin_id,
            topics: question.objective?.topic,
            marked_alternative: question.answer,
            time_spent: question.time,
            subject: question.objective?.subject,
          },
          {
            headers: {
              'If-Match': question.history._etag,
            },
          }
        );
        return response;
      } else {
        const response = await api.post(QUESTIONS_HISTORY_ENDPOINT, {
          qid: question.objective?.qid,
          user_email: loginData.user.email,
          scored: question.correct,
          origin: question.origin,
          origin_id: question.origin_id,
          topics: question.objective?.topic,
          marked_alternative: question.answer,
          time_spent: question.time,
          subject: question.objective?.subject,
        });
        return response;
      }
    } catch (error) {
      if (showError) {
        NotifyHelper.notifyError(Messages.FETCH_QUESTIONS_HISTORY_ERROR);
      }
    }
  }

  public async saveMultipleHistories(
    histories: ObjectiveHistory[]
  ): Promise<PostMultipleHistories | undefined> {
    const loginData = Authentication.loggedUser();
    if (!loginData) return;
    const response = await api.post<PostMultipleHistories>(
      QUESTIONS_HISTORY_ENDPOINT,
      histories
    );
    return response.data;
  }

  public async fetchQuestionsHistory({
    where = {},
    page,
    max = 200,
    sort = "[['_created', -1]]",
    headers = {},
    projection,
  }: FetchHistoryParams): Promise<
    AxiosResponse<EveResponse<ObjectiveHistory[]>> | undefined
  > {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const flattenQuery = this.flattenMongoQuery({
        query: {
          ...where,
          user_email: loginData.user.email,
        },
      }) as ObjectiveHistoryWhere;
      const response: AxiosResponse<EveResponse<ObjectiveHistory[]>> =
        await api.get(QUESTIONS_HISTORY_ENDPOINT, {
          params: {
            page,
            max_results: max,
            where: flattenQuery,
            sort,
            projection,
          },
          headers,
        });
      if (!response.data._items.length) return;
      return response;
    } catch (error) {
      console.error(error);
      NotifyHelper.notifyError(Messages.FETCH_QUESTIONS_HISTORY_ERROR);
    }
  }

  public async fetchDaysStudied(
    origin: 'objective' | 'evaluation' | 'simulation' = 'objective'
  ): Promise<AxiosResponse<EveResponse<string[]>> | undefined> {
    try {
      const loginData = Authentication.loggedUser();
      if (!loginData) return;
      const response: AxiosResponse<EveResponse<string[]>> = await api.get(
        `${QUESTIONS_HISTORY_ENDPOINT}/${loginData.user.email}/days/${origin}`
      );
      if (!response.data._items.length) return;
      return response;
    } catch (error) {
      console.error(error);
      NotifyHelper.notifyError(Messages.FETCH_QUESTIONS_HISTORY_ERROR);
    }
  }

  public async countQuestions({
    where = {},
    history = 0,
  }: FetchObjectivesParams): Promise<number> {
    //Check if there are any previous pending requests
    if (this.cancelToken) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    //Save the cancel token for the current request
    this.cancelToken = axios.CancelToken.source();

    const user = Authentication.loggedUser();
    if (!user) throw NoAuthError;
    if (!user.contest?._id) throw NoAuthError;
    const flattenQuery = this.flattenMongoQuery({ query: where }) as unknown;
    const axiosConfig: AxiosRequestConfig = {
      cancelToken: this.cancelToken.token,
      params: {
        where: flattenQuery,
        max_results: 1,
      },
      headers: {
        ContestId: user.contest._id,
        Useremail: user.user.email,
        Userstatus: Number(history),
      },
    };
    return api.head(QUESTIONS_ENDPOINT, axiosConfig).then((response) => {
      const _headers = response.headers as headers;
      return _headers['x-total-count'];
    });
  }
}

export default new Objectives();
